import React, { useContext, useMemo } from 'react'
import { makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import { ButtonPopupMenu } from '../components'
import { AddButton } from '../components/ButtonPopupMenu'
import { PresentationSectionSelectDialog } from '../dialogs'
import type { OptionsDocument, PresentationOptionV2, PresentationSectionV2 } from 'paintscout'
import type { LabelsOption } from '@paintscout/util/builder'
import isEqual from 'lodash/isEqual'
import { DialogStackContext } from '@paintscout/ui-business'
import { getAddableBlockGroups } from './addableBlockTypes'

interface AddBlockButtonProps {
  addPages: boolean
  classes?: StyleClasses<typeof useStyles>
  focused?: boolean
  // little sketch using id here instead of ref
  // TODO over to ref if need more than one of these with an id on a page
  id?: string
  objectLabels: LabelsOption
  onActionClick?: any
  options?: OptionsDocument
  onAddSection: (newSection: PresentationSectionV2) => void
  presentation: PresentationOptionV2
}

const useStyles = makeStyles<Theme, AddBlockButtonProps>(
  (theme: Theme) => {
    return {
      root: (props) => ({
        textAlign: 'center',
        position: 'relative',
        height: 0,
        display: 'flex',
        zIndex: props.focused ? 3 : 2,
        alignItems: 'center',
        justifyContent: 'center'
      }),
      menu: {
        boxShadow: theme.boxShadow[1]
      },
      button: (props) => ({
        color: theme.palette.presentation.sectionBorder.hover,
        borderStyle: 'solid',
        borderColor: theme.palette.presentation.sectionBorder.hover,
        borderWidth: '2px',
        backgroundColor: 'white',
        '&:hover': {
          backgroundColor: '#f5f5f5'
        },
        ...(props.focused && {
          color: theme.palette.presentation.sectionBorder.focus,
          borderColor: theme.palette.presentation.sectionBorder.focus
        })
      }),
      rule: {}
    }
  },
  { name: 'AddBlockButton' }
)

function AddBlockButton(props: AddBlockButtonProps) {
  const classes = useStyles(props)
  const { addPages, id, objectLabels, onActionClick, onAddSection, options, presentation } = props
  // one flat popup, in the same order as the sidebar's groups — the list is shared (addableBlockTypes.ts)
  const actions = useMemo(
    () =>
      getAddableBlockGroups({ options, objectLabels })
        .flatMap((group) => group.tiles)
        .map(({ type, label, icon }) => ({ key: type, label, icon })),
    [options, objectLabels]
  )

  const { openDialog, dismissDialog } = useContext(DialogStackContext)

  const handleAddSection = (ev) => {
    ev.stopPropagation()
    openDialog(PresentationSectionSelectDialog, {
      onConfirm: (newSection: PresentationSectionV2) => {
        onAddSection(newSection)
        dismissDialog()
      },
      presentation,
      onCancel: () => {
        dismissDialog()
      }
    })
  }

  if (addPages) {
    return (
      <div className={classes.root}>
        <div className={classes.rule}></div>
        <AddButton
          buttonSize={'small'}
          id={id}
          onClick={handleAddSection}
          className={classes.button}
          buttonVariant={'outlined'}
        />
      </div>
    )
  }

  return (
    <div className={classes.root}>
      <div className={classes.rule}></div>
      <ButtonPopupMenu
        actions={actions}
        buttonSize={'small'}
        buttonVariant={'outlined'}
        columnCount={3}
        id={id}
        onActionClick={onActionClick}
        tooltipTitle={`Add Block`}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
        {...({ classes: { button: classes.button, menu: classes.menu } } as any)}
      />
    </div>
  )
}

export default React.memo(AddBlockButton, (prevProps, nextProps) => {
  // keep this up to date with any new props
  if (prevProps.focused !== nextProps.focused) return false
  if (!isEqual(prevProps.presentation.advanced.style, nextProps.presentation.advanced.style)) return false
  if (!isEqual(prevProps.classes, nextProps.classes)) return false
  if (!isEqual(prevProps.objectLabels, nextProps.objectLabels)) return false
  if (prevProps.options !== nextProps.options) return false

  return true
})
